import { useMemo } from 'react';
import { logger } from '@/lib/logger';
import { useAuth } from '@/context/AuthContext';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8081';
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || 'jobportal';
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'job-frontend';

function base64UrlDecode(input: string) {
  input = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = input.length % 4;
  if (pad === 2) input += '==';
  else if (pad === 3) input += '=';
  else if (pad !== 0) return null;
  try {
    return atob(input);
  } catch {
    return null;
  }
}

function parseJwtPayload(token: string): any | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const payload = base64UrlDecode(parts[1]);
  if (!payload) return null;
  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

function isJwtExpiring(token: string, withinSeconds = 30): boolean {
  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return false;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp <= now + withinSeconds;
}

function shouldRetryOn401(method: string, endpoint: string) {
  const normalizedMethod = method.toUpperCase();
  if (['GET', 'HEAD', 'OPTIONS'].includes(normalizedMethod)) return true;
  return normalizedMethod === 'POST' && endpoint === '/api/employers';
}

// Generic API request function
const apiRequest = async (endpoint: string, options: RequestInit = {}, accessToken?: string) => {
  // Build headers but don't set Content-Type when body is FormData
  const headers: Record<string, string> = {};
  const body = options.body as any;

  if (!(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  // If the current access token is about to expire, refresh it before making the request.
  const currentToken = accessToken || localStorage.getItem('accessToken');
  if (currentToken && currentToken !== 'null' && currentToken !== 'undefined' && isJwtExpiring(currentToken)) {
    await tryRefreshToken();
  }

  // Prefer explicit accessToken param, fall back to localStorage
  const authToken = accessToken || localStorage.getItem('accessToken');
  if (authToken && authToken !== 'null' && authToken !== 'undefined') {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const method = (options.method || 'GET').toUpperCase();
  logger.api(method, url, {
    hasBody: !!options.body,
    bodyType: body instanceof FormData ? 'FormData' : typeof body,
  });

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...(options.headers as Record<string, string> || {}),
    },
  });

  // If unauthorized, try to refresh once and retry
  if (response.status === 401) {
    logger.warn('[api] 401 received, attempting token refresh...');
    const refreshed = await tryRefreshToken();
    if (refreshed && shouldRetryOn401(method, endpoint)) {
      const newToken = localStorage.getItem('accessToken');
      if (newToken) {
        // retry original request with new token
        const retryHeaders: Record<string, string> = {};
        if (!(body instanceof FormData)) retryHeaders['Content-Type'] = 'application/json';
        retryHeaders['Authorization'] = `Bearer ${newToken}`;

        const retry = await fetch(url, {
          ...options,
          headers: {
            ...retryHeaders,
            ...(options.headers as Record<string, string> || {}),
          },
        });

        if (!retry.ok) {
          const error = await buildApiError(retry);
          throw error;
        }

        return await parseResponseSafe(retry);
      }
    }

    // Refresh failed or retry not permitted -> clear tokens and notify app
    logger.warn('[api] Token refresh failed or request not retryable, clearing auth and dispatching logout');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.dispatchEvent(new CustomEvent('auth:logout'));

    const err = new Error('Unauthorized');
    (err as any).status = 401;
    throw err;
  }

  // If rate-limited (429), retry up to 2 times for idempotent GET requests with backoff
  if (response.status === 429 && ['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const retryAfterHeader = response.headers.get('Retry-After');
    const retryAfterMs = retryAfterHeader ? Math.min(parseInt(retryAfterHeader, 10) * 1000, 3000) : 1000;
    logger.warn(`[api] 429 Rate limited, retrying in ${retryAfterMs}ms...`);
    await new Promise((resolve) => setTimeout(resolve, retryAfterMs));

    const retryResponse = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...(options.headers as Record<string, string> || {}),
      },
    });

    if (retryResponse.ok) {
      return await parseResponseSafe(retryResponse);
    }
    const error = await buildApiError(retryResponse);
    throw error;
  }

  if (!response.ok) {
    const error = await buildApiError(response);
    throw error;
  }

  return await parseResponseSafe(response);
};

async function parseResponseSafe(response: Response) {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch (e) {
      const text = await response.text();
      return text;
    }
  }
  return await response.text();
}

async function buildApiError(response: Response) {
  let errorBody: any = null;
  try {
    errorBody = await response.json();
  } catch (e) {
    try {
      errorBody = await response.text();
    } catch (e2) {
      errorBody = null;
    }
  }

  const defaultMsg = response.status === 429
    ? 'Rate limit exceeded. Please wait a moment and try again.'
    : `API request failed with status ${response.status}`;

  const message = (errorBody && (errorBody.message || errorBody.error)) || (typeof errorBody === 'string' && errorBody.trim() ? errorBody : null) || defaultMsg;
  const error = new Error(message);
  (error as any).status = response.status;
  (error as any).response = errorBody;

  // Reduce severity for known "safe" errors (like duplicate applications)
  if (response.status === 400 && message && typeof message === 'string' && message.toLowerCase().includes('already applied')) {
    logger.warn('[api] User input validation', { status: response.status, message, body: errorBody });
  } else {
    logger.error('[api] ERROR', { status: response.status, message, body: errorBody });
  }
  return error;
}

async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return false;

  try {
    const tokenUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;
    const resp = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: KEYCLOAK_CLIENT_ID,
        refresh_token: refreshToken,
      }),
    });

    if (!resp.ok) {
      logger.warn('[api] Refresh token endpoint returned', { status: resp.status });
      return false;
    }

    const data = await resp.json();
    if (!data || !data.access_token) return false;

    localStorage.setItem('accessToken', data.access_token);
    if (data.refresh_token) localStorage.setItem('refreshToken', data.refresh_token);
    logger.info('[api] Token refreshed successfully');
    return true;
  } catch (e) {
    logger.error('[api] Error refreshing token', e);
    return false;
  }
}

// Factory to create API functions given an accessToken (usable in tests)
export const createApi = (accessToken?: string) => {
  return {
    // Auth API
    auth: {
      login: async (email: string, password: string) => {
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Login failed');
        }

        const data = await response.json();
        localStorage.setItem('accessToken', data.access_token);

        return {
          accessToken: data.access_token,
          user: data.user ? {
            sub: data.user.sub,
            email: data.user.email,
            name: data.user.firstName,
            preferred_username: data.user.preferred_username,
            realm_access: data.user.realm_access,
            roles: data.user.realm_access?.roles || []
          } : null
        };
      },

      register: async (email: string, password: string, role?: string) => {
        // Use backend user registration endpoint
        const response = await fetch(`${API_BASE_URL}/users/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password, role }),
        });

        if (!response.ok) {
          let errorMessage = "Registration failed";
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
            console.error("Registration error:", errorData);
          } catch (e) {
            const errorText = await response.text();
            console.error("Registration error (text):", errorText);
            errorMessage = `Registration failed (${response.status}): ${errorText.substring(0, 100)}`;
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();

        return {
          user: data.user,
          accessToken: data.access_token,
        };
      },

      logout: () => {
        window.dispatchEvent(new CustomEvent('auth:logout'));
      },

      getProfile: async () => {
        const response = await fetch(`${API_BASE_URL}/users/me`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch profile');
        }

        return await response.json();
      },

      hasRole: (role: string) => {
        const token = accessToken || localStorage.getItem('accessToken');
        if (!token) return false;
        try {
          // Parse JWT payload without bringing in external libraries if possible, or just use base64
          const payload = JSON.parse(atob(token.split('.')[1]));
          return payload?.realm_access?.roles?.includes(role) || false;
        } catch {
          return false;
        }
      },

      updateProfile: async (profileData: Partial<{
        email?: string;
        name?: string;
        firstName?: string;
        lastName?: string;
        phone?: string;
      }>) => {
        const response = await fetch(`${API_BASE_URL}/users/me`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify(profileData),
        });

        if (!response.ok) {
          throw new Error('Failed to update profile');
        }

        return await response.json();
      },

      changePassword: async (currentPassword: string, newPassword: string) => {
        const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ currentPassword, newPassword }),
        });

        if (!response.ok) {
          throw new Error('Failed to change password');
        }

        return await response.json();
      },

      forgotPassword: async (email: string) => {
        const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        });

        if (!response.ok) {
          throw new Error('Failed to send password reset email');
        }

        return await response.json();
      },

      resetPassword: async (token: string, newPassword: string) => {
        const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token, newPassword }),
        });

        if (!response.ok) {
          throw new Error('Failed to reset password');
        }

        return await response.json();
      },

      uploadResume: async (formData: FormData) => {
        const response = await fetch(`${API_BASE_URL}/users/upload-resume`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to upload resume');
        }

        return await response.json();
      },

      downloadResume: async (fileName: string) => {
        const response = await fetch(`${API_BASE_URL}/users/resume/${fileName}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to download resume');
        }

        return await response.blob();
      },

      // Generic methods for direct API access
      get: async (endpoint: string) => {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        if (!response.ok) throw new Error(`GET ${endpoint} failed`);
        return { data: await response.json() };
      },

      post: async (endpoint: string, data: any) => {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error(`POST ${endpoint} failed`);
        return { data: await response.json() };
      },

      put: async (endpoint: string, data: any) => {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error(`PUT ${endpoint} failed`);
        return { data: await response.json() };
      },

      delete: async (endpoint: string) => {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        if (!response.ok) throw new Error(`DELETE ${endpoint} failed`);
        return { data: await response.json() };
      },
    },

    // Job Seeker Profile API
    jobSeekerProfile: {
      get: async () => {
        return apiRequest('/users/job-seeker-profile', {}, accessToken);
      },
      create: async (data: Record<string, unknown>) => {
        return apiRequest('/users/job-seeker-profile', {
          method: 'POST',
          body: JSON.stringify(data),
        }, accessToken);
      },
      update: async (data: Record<string, unknown>) => {
        return apiRequest('/users/job-seeker-profile', {
          method: 'PUT',
          body: JSON.stringify(data),
        }, accessToken);
      },
      exists: async () => {
        return apiRequest('/users/job-seeker-profile/exists', {}, accessToken);
      },
      delete: async () => {
        return apiRequest('/users/job-seeker-profile', {
          method: 'DELETE',
        }, accessToken);
      },
    },

    // User Settings API
    userSettings: {
      get: async () => {
        return apiRequest('/users/settings', {}, accessToken);
      },
      update: async (data: Record<string, unknown>) => {
        return apiRequest('/users/settings', {
          method: 'PUT',
          body: JSON.stringify(data),
        }, accessToken);
      },
      deleteAccount: async () => {
        return apiRequest('/users/settings/account', {
          method: 'DELETE',
        }, accessToken);
      },
    },

    // Two-Factor Authentication API
    twoFactor: {
      getStatus: async () => {
        return apiRequest('/users/2fa/status', {}, accessToken);
      },
      enable: async () => {
        return apiRequest('/users/2fa/enable', { method: 'POST' }, accessToken);
      },
      disable: async () => {
        return apiRequest('/users/2fa/disable', { method: 'POST' }, accessToken);
      },
      getSetup: async () => {
        return apiRequest('/users/2fa/setup', {}, accessToken);
      },
      verifySetup: async (secret: string, code: string) => {
        return apiRequest('/users/2fa/verify-setup', {
          method: 'POST',
          body: JSON.stringify({ secret, code }),
        }, accessToken);
      },
    },

    // Jobs API
    jobs: {
      // Get all jobs with pagination and filters
      getJobs: async (params: {
        q?: string;
        type?: string;
        location?: string;
        minSalary?: number;
        maxSalary?: number;
        page?: number;
        size?: number;
      } = {}) => {
        const searchParams = new URLSearchParams();

        // Add filters if they have values
        if (params.q) searchParams.append('q', params.q);
        if (params.type) searchParams.append('type', params.type);
        if (params.location) searchParams.append('location', params.location);
        if (params.minSalary) searchParams.append('minSalary', params.minSalary.toString());
        if (params.maxSalary) searchParams.append('maxSalary', params.maxSalary.toString());
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.size) searchParams.append('size', params.size.toString());

        const response = await apiRequest(`/jobs?${searchParams.toString()}`);

        // Transform Spring Page response to frontend format
        return {
          content: response.content || [],
          totalElements: response.totalElements || 0,
          totalPages: response.totalPages || 0,
          size: response.size || params.size || 12,
          number: response.number || params.page || 0,
          first: response.first !== false,
          last: response.last !== false,
        };
      },

      // Get jobs for current employer (Employer only)
      getMyJobs: async (params: {
        employerId: string;
        q?: string;
        status?: string;
        type?: string;
        page?: number;
        size?: number;
      }) => {
        const searchParams = new URLSearchParams();

        searchParams.append('employerId', params.employerId);
        if (params.q) searchParams.append('q', params.q);
        if (params.status) searchParams.append('status', params.status);
        if (params.type) searchParams.append('type', params.type);
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.size) searchParams.append('size', params.size.toString());

        return apiRequest(`/jobs/my-jobs?${searchParams.toString()}`, {}, accessToken);
      },

      // Get job by ID
      getJob: async (id: string) => {
        return apiRequest(`/jobs/${id}`);
      },

      // Create new job (Employer/Admin only)
      createJob: async (jobData: {
        employerId: string;
        title: string;
        description: string;
        location: string;
        jobType: string;
        salaryMin: number;
        salaryMax: number;
        requirements: string;
        // optional expiry ISO string
        expiresAt?: string;
      }) => {
        return apiRequest('/jobs', {
          method: 'POST',
          body: JSON.stringify(jobData),
        }, accessToken);
      },

      // Update job (Employer/Admin only)
      updateJob: async (id: string, jobData: Partial<{
        title: string;
        description: string;
        location: string;
        jobType: string;
        salaryMin: number;
        salaryMax: number;
        requirements: string;
      }>) => {
        return apiRequest(`/jobs/${id}`, {
          method: 'PUT',
          body: JSON.stringify(jobData),
        }, accessToken);
      },

      // Delete job (Employer/Admin only)
      deleteJob: async (id: string) => {
        return apiRequest(`/jobs/${id}`, {
          method: 'DELETE',
        }, accessToken);
      },

      // Get job counts grouped by category (public, no auth)
      getCategoryCounts: async (): Promise<Record<string, number>> => {
        return apiRequest('/jobs/category-counts');
      },
    },

    // Applications API
    applications: {
      // Get current user's applications (Candidate only)
      getMyApplications: async (params: {
        jobId?: string;
        status?: string;
        page?: number;
        size?: number;
      } = {}) => {
        const searchParams = new URLSearchParams();

        if (params.jobId) searchParams.append('jobId', params.jobId);
        if (params.status) searchParams.append('status', params.status);
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.size) searchParams.append('size', params.size.toString());

        return apiRequest(`/apps/my-applications?${searchParams.toString()}`, {}, accessToken);
      },

      // Get employer's applications (Employer only)
      getEmployerApplications: async (params: {
        employerId: string;
        jobId?: string;
        status?: string;
        page?: number;
        size?: number;
      }) => {
        const searchParams = new URLSearchParams();
        searchParams.append('employerId', params.employerId);
        if (params.jobId) searchParams.append('jobId', params.jobId);
        if (params.status) searchParams.append('status', params.status);
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.size) searchParams.append('size', params.size.toString());

        return apiRequest(`/apps/employer/my-applications?${searchParams.toString()}`, {}, accessToken);
      },

      // Apply to job (Candidate only) - ensure employerId is included
      applyToJob: async (jobData: {
        jobId: string;
        coverLetter: string;
        resumeUrl?: string;
        employerId?: string; // optional; will be fetched if not provided
      }) => {
        let { employerId } = jobData as { employerId?: string };

        // If employerId not provided, fetch job to obtain it
        if (!employerId) {
          try {
            const job = await apiRequest(`/jobs/${jobData.jobId}`);
            employerId = job?.employerId;
          } catch (e) {
            // If fetching job fails, bubble up a helpful error
            throw new Error('Failed to resolve employer for job. Please try again.');
          }
        }

        const body = {
          jobId: jobData.jobId,
          employerId,
          coverLetter: jobData.coverLetter,
          resumeUrl: jobData.resumeUrl,
        };

        return apiRequest('/apps', {
          method: 'POST',
          body: JSON.stringify(body),
        }, accessToken);
      },

      // Update application status (Employer only)
      updateApplication: async (id: string, status: string, notes?: string) => {
        return apiRequest(`/apps/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ status, notes }),
        }, accessToken);
      },

      // Get single application by ID
      getApplication: async (id: string) => {
        return apiRequest(`/apps/${id}`, {}, accessToken);
      },

      // Delete application (Candidate only)
      deleteApplication: async (id: string) => {
        return apiRequest(`/apps/${id}`, {
          method: 'DELETE',
        }, accessToken);
      },
    },

    // Notifications API - added to match backend
    notifications: {
      getNotifications: async (params: {
        status?: string;
        page?: number;
        size?: number;
      } = {}) => {
        const searchParams = new URLSearchParams();
        if (params.status) searchParams.append('status', params.status);
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.size) searchParams.append('size', params.size.toString());

        return apiRequest(`/notifications?${searchParams.toString()}`, {}, accessToken);
      },

      getUnreadCount: async () => {
        return apiRequest('/notifications/unread-count', {}, accessToken);
      },

      markAsRead: async (id: string) => {
        return apiRequest(`/notifications/${id}/read`, {
          method: 'PUT',
        }, accessToken);
      },

      // Bulk mark all unread notifications as read (server-side endpoint)
      markAllAsRead: async () => {
        return apiRequest('/notifications/mark-all-read', {
          method: 'PUT',
        }, accessToken);
      },

      // Undo mark-as-read for a list of notification IDs
      undoMarkRead: async (ids: string[]) => {
        return apiRequest('/notifications/undo-mark-read', {
          method: 'PUT',
          body: JSON.stringify(ids),
        }, accessToken);
      },

      // Send email from employer to candidate
      sendEmail: async (data: { to: string; subject: string; message: string }) => {
        return apiRequest('/notifications/send-email', {
          method: 'POST',
          body: JSON.stringify(data),
        }, accessToken);
      },

    },

    // Employers API
    employers: {
      // Get all employers with search
      getEmployers: async (params: {
        q?: string;
        page?: number;
        size?: number;
        sort?: string;
      } = {}) => {
        const searchParams = new URLSearchParams();

        if (params.q) searchParams.append('q', params.q);
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.size) searchParams.append('size', params.size.toString());
        if (params.sort) searchParams.append('sort', params.sort);

        return apiRequest(`/employers?${searchParams.toString()}`, {}, accessToken);
      },

      // Get featured/public employers
      getPublicEmployers: async (params: {
        size?: number;
      } = {}) => {
        const searchParams = new URLSearchParams();
        if (params.size) searchParams.append('size', params.size.toString());

        return apiRequest(`/employers/public?${searchParams.toString()}`);
      },

      // Get employer by ID
      getEmployer: async (id: string) => {
        return apiRequest(`/employers/${id}`);
      },

      // Get current employer profile (Employer only)
      getCurrentEmployer: async () => {
        return apiRequest('/employers/me', {}, accessToken);
      },

      // Create employer profile (Employer only)
      createEmployer: async (employerData: {
        userId?: string;
        companyName: string;
        email: string;
        phone?: string;
        description?: string;
        website?: string;
        industry?: string;
        companySize?: string;
        location?: string;
        state?: string;
        city?: string;
        logoUrl?: string;
      }) => {
        return apiRequest('/employers', {
          method: 'POST',
          body: JSON.stringify(employerData),
        }, accessToken);
      },

      // Upload employer logo file (multipart)
      uploadLogo: async (id: string, file: File) => {
        const fd = new FormData();
        fd.append('file', file);
        return apiRequest(`/employers/${id}/logo`, {
          method: 'POST',
          body: fd,
        }, accessToken);
      },

      // Update employer profile (Employer only)
      updateEmployer: async (id: string, employerData: Partial<{
        companyName: string;
        email: string;
        phone: string;
        description: string;
        website: string;
        industry: string;
        companySize: string;
        location: string;
        logoUrl: string;
        verificationStatus: string;
      }>) => {
        return apiRequest(`/employers/${id}`, {
          method: 'PUT',
          body: JSON.stringify(employerData),
        }, accessToken);
      },
      // Delete employer profile (Admin only)
      deleteEmployer: async (id: string) => {
        return apiRequest(`/employers/${id}`, {
          method: 'DELETE',
        }, accessToken);
      },
    },

    // Admin Candidates API (Admin only)
    adminCandidates: {
      getCandidates: async (params: { page?: number; size?: number } = {}) => {
        const searchParams = new URLSearchParams();
        if (params.page !== undefined) searchParams.append('page', params.page.toString());
        if (params.size !== undefined) searchParams.append('size', params.size.toString());
        return apiRequest(`/users/admin/candidates?${searchParams.toString()}`, {}, accessToken);
      },
      lockCandidate: async (id: string) => {
        return apiRequest(`/users/admin/candidates/${id}/lock`, { method: 'PUT' }, accessToken);
      },
      unlockCandidate: async (id: string) => {
        return apiRequest(`/users/admin/candidates/${id}/unlock`, { method: 'PUT' }, accessToken);
      },
      deleteCandidate: async (id: string) => {
        return apiRequest(`/users/admin/candidates/${id}`, { method: 'DELETE' }, accessToken);
      },
    },

    // Platform Stats API — aggregates counts from all services
    platformStats: {
      getStats: async (): Promise<{ jobSeekers: number; companies: number; jobsPosted: number; applications: number }> => {
        const [users, employers, jobs, apps] = await Promise.all([
          apiRequest('/users/count').catch(() => ({ count: 0 })),
          apiRequest('/employers/count').catch(() => ({ count: 0 })),
          apiRequest('/jobs/count').catch(() => ({ count: 0 })),
          apiRequest('/apps/count').catch(() => ({ count: 0 })),
        ]);
        return {
          jobSeekers: users.count ?? 0,
          companies: employers.count ?? 0,
          jobsPosted: jobs.count ?? 0,
          applications: apps.count ?? 0,
        };
      },
    },

    // Search API
    search: {
      // Advanced job search
      searchJobs: async (params: {
        q?: string;
        jobType?: string;
        location?: string;
        minSalary?: number;
        maxSalary?: number;
        employerId?: string;
        experienceLevels?: string[]; // ENTRY | MID | SENIOR | LEAD
        page?: number;
        size?: number;
      } = {}) => {
        const searchParams = new URLSearchParams();

        if (params.q) searchParams.append('q', params.q);
        if (params.jobType) searchParams.append('jobType', params.jobType);
        if (params.location) searchParams.append('location', params.location);
        if (params.minSalary) searchParams.append('minSalary', params.minSalary.toString());
        if (params.maxSalary) searchParams.append('maxSalary', params.maxSalary.toString());
        if (params.employerId) searchParams.append('employerId', params.employerId);
        if (params.experienceLevels && params.experienceLevels.length > 0) searchParams.append('experienceLevels', params.experienceLevels.join(','));
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.size) searchParams.append('size', params.size.toString());

        return apiRequest(`/search/jobs?${searchParams.toString()}`);
      },

      // Advanced candidate search
      searchCandidates: async (params: {
        q?: string;
        location?: string;
        jobType?: string;
        experience?: string;
        education?: string;
        skills?: string;
        salaryMin?: string | number;
        salaryMax?: string | number;
        page?: number;
        size?: number;
      } = {}) => {
        const searchParams = new URLSearchParams();

        if (params.q) searchParams.append('q', params.q);
        if (params.location) searchParams.append('location', params.location);
        if (params.jobType) searchParams.append('jobType', params.jobType);
        if (params.experience) searchParams.append('experience', params.experience);
        if (params.education) searchParams.append('education', params.education);
        if (params.skills) searchParams.append('skills', params.skills);
        if (params.salaryMin) searchParams.append('minSalary', params.salaryMin.toString());
        if (params.salaryMax) searchParams.append('maxSalary', params.salaryMax.toString());
        if (params.page !== undefined && params.page !== null) searchParams.append('page', params.page.toString());
        if (params.size !== undefined && params.size !== null) searchParams.append('size', params.size.toString());

        return apiRequest(`/search/candidates?${searchParams.toString()}`);
      },
    },

    // Community API
    community: {
      // Components
      components: {
        list: async (params: {
          q?: string;
          category?: string;
          page?: number;
          size?: number;
        } = {}) => {
          const searchParams = new URLSearchParams();
          if (params.q) searchParams.append('q', params.q);
          if (params.category) searchParams.append('category', params.category);
          if (params.page) searchParams.append('page', params.page.toString());
          if (params.size) searchParams.append('size', params.size.toString());

          const queryString = searchParams.toString();
          return apiRequest(`/community/components${queryString ? `?${queryString}` : ''}`, {}, accessToken);
        },
        get: async (id: string) => {
          return apiRequest(`/community/components/${id}`, {}, accessToken);
        },
        create: async (componentData: {
          name: string;
          description: string;
          category: string;
          author: string;
          version: string;
          tags: string[];
          repositoryUrl?: string;
          documentationUrl?: string;
        }) => {
          return apiRequest('/community/components', {
            method: 'POST',
            body: JSON.stringify(componentData),
          }, accessToken);
        },
        delete: async (id: string) => {
          return apiRequest(`/community/components/${id}`, {
            method: 'DELETE',
          }, accessToken);
        },
      },
      // Services
      services: {
        list: async (params: {
          q?: string;
          category?: string;
          page?: number;
          size?: number;
        } = {}) => {
          const searchParams = new URLSearchParams();
          if (params.q) searchParams.append('q', params.q);
          if (params.category) searchParams.append('category', params.category);
          if (params.page) searchParams.append('page', params.page.toString());
          if (params.size) searchParams.append('size', params.size.toString());

          const queryString = searchParams.toString();
          return apiRequest(`/community/services${queryString ? `?${queryString}` : ''}`, {}, accessToken);
        },
        get: async (id: string) => {
          return apiRequest(`/community/services/${id}`, {}, accessToken);
        },
        create: async (serviceData: {
          name: string;
          description: string;
          category: string;
          provider: string;
          version: string;
          tags: string[];
          apiUrl?: string;
          documentationUrl?: string;
          status?: 'ACTIVE' | 'BETA' | 'DEPRECATED';
        }) => {
          return apiRequest('/community/services', {
            method: 'POST',
            body: JSON.stringify(serviceData),
          }, accessToken);
        },
        delete: async (id: string) => {
          return apiRequest(`/community/services/${id}`, {
            method: 'DELETE',
          }, accessToken);
        },
      },
    },

    // Blog API
    blog: {
      // Components
      components: {
        list: async (params: {
          q?: string;
          category?: string;
          page?: number;
          size?: number;
        } = {}) => {
          const searchParams = new URLSearchParams();
          if (params.q) searchParams.append('q', params.q);
          if (params.category) searchParams.append('category', params.category);
          if (params.page) searchParams.append('page', params.page.toString());
          if (params.size) searchParams.append('size', params.size.toString());

          const queryString = searchParams.toString();
          return apiRequest(`/blog/components${queryString ? `?${queryString}` : ''}`, {}, accessToken);
        },
        get: async (id: string) => {
          return apiRequest(`/blog/components/${id}`, {}, accessToken);
        },
        create: async (componentData: {
          name: string;
          description: string;
          category: string;
          author: string;
          version: string;
          tags: string[];
          repositoryUrl?: string;
          documentationUrl?: string;
          blogPostUrl?: string;
        }) => {
          return apiRequest('/blog/components', {
            method: 'POST',
            body: JSON.stringify(componentData),
          }, accessToken);
        },
        delete: async (id: string) => {
          return apiRequest(`/blog/components/${id}`, {
            method: 'DELETE',
          }, accessToken);
        },
      },
      // Services
      services: {
        list: async (params: {
          q?: string;
          category?: string;
          page?: number;
          size?: number;
        } = {}) => {
          const searchParams = new URLSearchParams();
          if (params.q) searchParams.append('q', params.q);
          if (params.category) searchParams.append('category', params.category);
          if (params.page) searchParams.append('page', params.page.toString());
          if (params.size) searchParams.append('size', params.size.toString());

          const queryString = searchParams.toString();
          return apiRequest(`/blog/services${queryString ? `?${queryString}` : ''}`, {}, accessToken);
        },
        get: async (id: string) => {
          return apiRequest(`/blog/services/${id}`, {}, accessToken);
        },
        create: async (serviceData: {
          name: string;
          description: string;
          category: string;
          provider: string;
          version: string;
          tags: string[];
          apiUrl?: string;
          documentationUrl?: string;
          blogPostUrl?: string;
          status?: 'ACTIVE' | 'BETA' | 'DEPRECATED';
        }) => {
          return apiRequest('/blog/services', {
            method: 'POST',
            body: JSON.stringify(serviceData),
          }, accessToken);
        },
        delete: async (id: string) => {
          return apiRequest(`/blog/services/${id}`, {
            method: 'DELETE',
          }, accessToken);
        },
      },
    },
    // Candidates public lookup API
    candidates: {
      getCandidateByUser: async (userId: string) => {
        return apiRequest(`/public/candidates/user/${userId}`, {}, accessToken);
      },
      getCandidateById: async (id: number | string) => {
        return apiRequest(`/public/candidates/${id}`, {}, accessToken);
      },
    },
    // AI Job Agent API
    aiAgent: {
      getMatches: async (minScore?: number) => {
        const params = new URLSearchParams();
        if (minScore) params.append('minScore', minScore.toString());
        const queryString = params.toString();
        return apiRequest(`/notifications/ai-matches${queryString ? `?${queryString}` : ''}`, {}, accessToken);
      },
      getStatus: async () => {
        return apiRequest('/notifications/agent-status', {}, accessToken);
      },
      refreshMatches: async (minScore?: number) => {
        const params = new URLSearchParams();
        if (minScore !== undefined && minScore !== null) params.append('minScore', minScore.toString());
        const queryString = params.toString();
        return apiRequest(`/notifications/ai-matches/refresh${queryString ? `?${queryString}` : ''}`, {
          method: 'POST',
        }, accessToken);
      },
    },
  };
};

// Hook to get API functions with auth token
export const useApi = () => {
  const { accessToken } = useAuth();
  return useMemo(() => createApi(accessToken), [accessToken]);
};


// Type definitions
export interface Job {
  id: string;
  employerId: string;
  employerName?: string;
  title: string;
  description: string;
  location: string;
  jobType: 'FULL_TIME' | 'PART_TIME' | 'CONTRACT' | 'REMOTE';
  salaryMin: number;
  salaryMax: number;
  requirements: string;
  createdAt: string;
  expiresAt: string;
  status: 'ACTIVE' | 'CLOSED' | 'DRAFT';
}

export interface Application {
  id: string;
  jobId: string;
  userId: string;
  employerId: string;
  coverLetter: string;
  resumeUrl?: string;
  status: 'PENDING' | 'REVIEWED' | 'ACCEPTED' | 'REJECTED' | 'WITHDRAWN';
  notes?: string;
  appliedAt: string;
  updatedAt: string;
}

export interface Employer {
  id: string;
  userId: string;
  companyName: string;
  email: string;
  phone?: string;
  description?: string;
  website?: string;
  industry?: string;
  companySize?: string;
  location?: string;
  city?: string;
  state?: string;
  logoUrl?: string;
  verificationStatus?: 'PENDING' | 'VERIFIED' | 'REJECTED';
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
};
